declare global {
  interface Window {
    enVGSFields?: Record<string, { css: Record<string, string | Record<string, string>> }>
  }
}

export class ClientScripts {
  private pageType: string | undefined
  private pageNumber: number | undefined
  private pageCount: number | undefined

  constructor() {
    if (!window.pageJson) {
      console.warn('pageJson is not defined on window')
      return
    }
    this.pageType = window.pageJson.pageType
    this.pageNumber = window.pageJson.pageNumber
    this.pageCount = window.pageJson.pageCount

    if (!this.pageType || !this.pageNumber || !this.pageCount) {
      console.warn('pageType, pageNumber, or pageCount is not defined in pageJson')
      return
    }

    if (this.pageType === 'donation' && this.pageNumber === this.pageCount) {
      this.confirmationPage()
    }

    this.setVgsStyles()
    this.setHeaderLogo()
    document.addEventListener('DOMContentLoaded', () => {
      this.handleAnnualGift()
      this.handleInMemFrequency()
    })
  }

  private setVgsStyles(): void {
    // VGS card fields render in cross-origin iframes, so they can't use the
    // page's loaded fonts — each needs its own @font-face + gstatic woff2 URL.
    // The LWR theme (class on <html>) uses Source Sans 3 and the LWR input
    // padding (--input-padding-y: 0.75em); the default theme uses Host Grotesk.
    const isLwr = document.documentElement.classList.contains('lwr')

    const lwrVgsFieldCss = {
      color: 'oklch(0.18 0.02 260)',
      padding: '0.75em 1.5em',
      'font-size': '16px',
      '@font-face': {
        'font-family': 'Source Sans 3',
        'font-style': 'normal',
        // Variable font — Google declares this file across the 200–900 range.
        'font-weight': '200 900',
        'font-display': 'swap',
        src: "url(https://fonts.gstatic.com/s/sourcesans3/v19/nwpStKy2OAdR1K-IwhWudF-R3w8aZejf5Hc.woff2) format('woff2')",
        'unicode-range': 'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD',
      },
      'font-family': '"Source Sans 3", sans-serif',
    }

    const defaultVgsFieldCss = {
      color: 'oklch(0.18 0.02 260)',
      padding: '1em 1.5em',
      'font-size': '17px',
      '@font-face': {
        'font-family': 'Host Grotesk',
        'font-style': 'normal',
        'font-weight': '400',
        'font-display': 'swap',
        src: "url(https://fonts.gstatic.com/s/hostgrotesk/v5/co3BmWBnlCJ3U42vbbfdwMjZpFjm.woff2) format('woff2')",
        'unicode-range': 'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD',
      },
      'font-family': '"Host Grotesk", sans-serif',
    }

    const vgsFieldCss = isLwr ? lwrVgsFieldCss : defaultVgsFieldCss

    window.enVGSFields = {
      'transaction.ccnumber': { css: vgsFieldCss },
      'transaction.ccexpire': { css: vgsFieldCss },
      'transaction.ccvv': { css: vgsFieldCss },
    }
  }

  private confirmationPage(): void {
    const tributeSection = document.getElementById('confirmation-tribute')
    const informSection = document.getElementById('confirmation-inform')

    // Show tribute section if honoree name is populated
    if (tributeSection) {
      const honoreeName = tributeSection.querySelector('#honoree-name')
      const honoreeText = honoreeName?.textContent?.trim()
      if (honoreeText && honoreeText !== '{receipt_data~inMemhonoreeName}') {
        tributeSection.classList.remove('hidden')
      }
    }

    // Show inform section if inform name is populated
    if (informSection) {
      const informName = informSection.querySelector('span')
      const informText = informName?.textContent?.trim()
      if (informText && informText !== '{receipt_data~inMeminformName}') {
        informSection.classList.remove('hidden')
      }
    }
  }

  private setHeaderLogo(): void {
    const variableHeaderLogo = document.querySelector('.header-logo-variable img') as HTMLImageElement
    const headerLogo = document.querySelector('.header-logo-img') as HTMLImageElement
    if (variableHeaderLogo && headerLogo) {
      headerLogo.src = variableHeaderLogo.src
    }
  }

  /**
   * Uncheck the "dedicate gift" (transaction.inmem) checkbox whenever the
   * recurring-frequency radio group is anything other than ONE_TIME.
   *
   * Listens to the `frequency-change` event dispatched by zuri-en's Frequency
   * module — this covers user clicks on the radios AND programmatic changes
   * triggered by EN form-dependency rules (e.g. the Annual Gift Upgrade
   * checkbox flipping the radio to ANNUAL via a "Set Value" rule).
   *
   * Dispatches a bubbling `change` event after toggling the checkbox so other
   * listeners (zuri-en's ECardPicker collapse, EN dependency rules, etc.)
   * react to the programmatic uncheck the same way they would to a user click.
   */
  private handleInMemFrequency(): void {
    const inMemCheckbox = document.querySelector<HTMLInputElement>('#en__field_transaction_inmem')
    if (!inMemCheckbox) {
      console.warn('In-memory tribute checkbox not found')
      return
    }

    // Must match the `eCard.picker.deliveryOptionsSelector` value passed to ZuriEn
    // in main.ts — same field, two consumers.
    const DELIVERY_OPTIONS_SELECTOR = '.en__field--NOT_TAGGED_7'

    const uncheckIfNotOneTime = (frequency: string) => {
      if (frequency === 'ONE_TIME') return

      if (inMemCheckbox.checked) {
        inMemCheckbox.checked = false
        // `change` covers EN form-dependency rules and any generic change listeners.
        inMemCheckbox.dispatchEvent(new Event('change', { bubbles: true }))
        // `click` is what zuri-en's ECardPicker listens for on this checkbox,
        // and its handler is responsible for hiding the eCard picker collapse.
        inMemCheckbox.dispatchEvent(new Event('click', { bubbles: true }))
      }

      // Re-select "No e-card" (value="N") so the delivery-options radio group
      // stays in sync with the (now-hidden) eCard picker collapse. Without this,
      // a user could have had "Send an e-card" checked when the recurring
      // upgrade fired, leaving the form data inconsistent with what's visible.
      const noECardRadio = document.querySelector<HTMLInputElement>(`${DELIVERY_OPTIONS_SELECTOR} input[type="radio"][value="N"]`)
      if (noECardRadio && !noECardRadio.checked) {
        noECardRadio.checked = true
        noECardRadio.dispatchEvent(new Event('change', { bubbles: true }))
        noECardRadio.dispatchEvent(new Event('click', { bubbles: true }))
      }
    }

    document.body.addEventListener('frequency-change', (event) => {
      const freq = (event as CustomEvent).detail?.frequency as string | undefined
      if (freq) uncheckIfNotOneTime(freq)
    })

    // Initial sync — covers the case where the page renders with a
    // non-ONE_TIME radio pre-checked and inmem also somehow checked.
    const checkedRadio = document.querySelector<HTMLInputElement>('input[name="transaction.recurrfreq"]:checked')
    if (checkedRadio) uncheckIfNotOneTime(checkedRadio.value)
  }

  private handleAnnualGift(): void {
    const annualGiftCheckbox = document.querySelector<HTMLInputElement>('.en__field--NOT_TAGGED_39 .en__field__input--checkbox')
    const annualGiftRadio = document.querySelector<HTMLInputElement>('.en__field--recurrfreq .en__field__input--radio[value="ANNUAL"]')
    const monthlyGiftRadio = document.querySelector<HTMLInputElement>('.en__field--recurrfreq .en__field__input--radio[value="MONTHLY"]')

    if (!annualGiftCheckbox || !annualGiftRadio || !monthlyGiftRadio) {
      console.warn('Annual gift checkbox or radio buttons not found')
      return
    }

    // When the annual gift checkbox is toggled, update the radio button selection
    annualGiftCheckbox.addEventListener('change', () => {
      if (annualGiftCheckbox.checked) {
        const event = new CustomEvent('frequency-change', { detail: { frequency: 'ANNUAL' } })
        document.body.dispatchEvent(event)
      } else {
        annualGiftRadio.checked = false
      }
    })
  }

  private lastPageFeeCover(): void {
    
  }
}
